<template>
    <div class="wrapper">
        <div class="main visual-2 doc-profile-main">
            <Header />

            <div class="profile-doctor">
                <div class="doctor-container page-content">

                    <!-- Main Wizard Card -->
                    <div class="wizard-card">

                        <div class="page-header mb-2">
                            <h3 class="page-title"><i class="fa-solid fa-user-doctor"></i> Add New Doctor</h3>
                            <p class="text-muted">Let's build a doctor profile together.</p>
                        </div>

                        <!-- Progress visual - Now only 2 steps -->
                        <div class="wizard-progress mb-4">
                            <div class="step-item" :class="{ active: currentStep >= 0, completed: currentStep > 0 }">
                                <div class="step-circle">1</div>
                                <span>Profile</span>
                            </div>
                            <div class="step-line" :class="{ filled: currentStep > 0 }"></div>
                            <div class="step-item" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                                <div class="step-circle">2</div>
                                <span>Schedule</span>
                            </div>
                        </div>

                        <!-- Assistant Bubble -->
                        <div class="conversation-header mb-4">
                            <div class="avatar-assistant">
                                <i class="fa-solid fa-robot"></i>
                            </div>
                            <div class="assistant-bubble">
                                <transition name="fade" mode="out-in">
                                    <div :key="currentStep">{{ conversationMessage }}</div>
                                </transition>
                            </div>
                        </div>

                        <div class="wizard-body">

                            <!-- Step 1: Profile Details (All in one) -->
                            <div v-if="currentStep === 0" class="step-content fade-in">
                                <div class="sub-step">
                                    <div class="row g-3">
                                        <!-- Q1: Name -->
                                        <div class="col-md-6">
                                            <label class="form-label-theme">First Name <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.firstname" @blur="validateField('firstname')"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.firstname }"
                                                placeholder="e.g. John" autofocus />
                                            <div v-if="validationErrors.firstname" class="validation-error">
                                                {{ validationErrors.firstname }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label-theme">Last Name <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.lastname" @blur="validateField('lastname')"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.lastname }"
                                                placeholder="e.g. Doe" />
                                            <div v-if="validationErrors.lastname" class="validation-error">
                                                {{ validationErrors.lastname }}
                                            </div>
                                        </div>

                                        <!-- Q2: Professional -->
                                        <div class="col-md-4">
                                            <label class="form-label-theme">Speciality <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.speciality" @blur="validateField('speciality')"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.speciality }"
                                                placeholder="e.g. Cardiologist" />
                                            <div v-if="validationErrors.speciality" class="validation-error">
                                                {{ validationErrors.speciality }}
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label-theme">Department Code <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.departmentCode"
                                                @blur="validateField('departmentCode')" @input="formatDepartmentCode"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.departmentCode }"
                                                placeholder="e.g. CD" maxlength="2" />
                                            <div v-if="validationErrors.departmentCode" class="validation-error">
                                                {{ validationErrors.departmentCode }}
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label-theme">Medical Degree <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.medicalDegree" @blur="validateField('medicalDegree')"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.medicalDegree }"
                                                placeholder="e.g. MBBS, FCPS" />
                                            <div v-if="validationErrors.medicalDegree" class="validation-error">
                                                {{ validationErrors.medicalDegree }}
                                            </div>
                                        </div>

                                        <div class="col-md-4">
                                            <label class="form-label-theme">
                                                External Systems Id
                                                <span class="text-muted">(Optional)</span>
                                            </label>
                                            <input v-model="doctor.reference" @blur="validateField('reference')"
                                                type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.reference }"
                                                placeholder="e.g. aprPXYZ" />
                                            <div v-if="validationErrors.reference" class="validation-error">
                                                {{ validationErrors.reference }}
                                            </div>
                                        </div>

                                        <!-- Q3: Contact & Personal -->
                                        <div class="col-md-4">
                                            <label class="form-label-theme">Gender <span
                                                    class="required">*</span></label>
                                            <select v-model="doctor.gender" @change="validateField('gender')"
                                                class="form-select-theme"
                                                :class="{ 'is-invalid': validationErrors.gender }">
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                            <div v-if="validationErrors.gender" class="validation-error">
                                                {{ validationErrors.gender }}
                                            </div>
                                        </div>
                                        <!-- FHIR ID -->

                                        <div class="col-md-4">
                                            <label class="form-label-theme">Phone <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.phone"
                                                @input="formatPhone" type="text" class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.phone }"
                                                :placeholder="phonePlaceholder" minlength="10" maxlength="16" />
                                            <div v-if="validationErrors.phone" class="validation-error">
                                                {{ validationErrors.phone }}
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label-theme">Email <span
                                                    class="required">*</span></label>
                                            <input v-model="doctor.email" @blur="validateField('email')" type="email"
                                                class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.email }"
                                                placeholder="doctor@example.com" />
                                            <div v-if="validationErrors.email" class="validation-error">
                                                {{ validationErrors.email }}
                                            </div>
                                        </div>

                                        <!-- Languages - Better Multi-select -->
                                        <div class="col-md-6">
                                            <label class="form-label-theme">Languages <span
                                                    class="required">*</span></label>
                                            <div class="multiselect-wrapper">
                                                <div class="selected-languages" @click="toggleLanguageDropdown"
                                                    :class="{ 'is-invalid': validationErrors.languagesSpoken }">
                                                    <span v-if="doctor.languagesSpoken.length === 0"
                                                        class="placeholder">Select languages...</span>
                                                    <span v-else class="selected-tags">
                                                        <span v-for="lang in doctor.languagesSpoken" :key="lang"
                                                            class="language-tag">
                                                            {{ lang }}
                                                            <span class="remove-tag"
                                                                @click.stop="removeLanguage(lang)">×</span>
                                                        </span>
                                                    </span>
                                                    <i class="fa-solid fa-chevron-down dropdown-icon"></i>
                                                </div>
                                                <div v-if="showLanguageDropdown" class="language-dropdown">
                                                    <div class="language-search" v-if="filteredLanguages.length > 5">
                                                        <input v-model="languageSearch" type="text"
                                                            placeholder="Search languages..." class="search-input" />
                                                    </div>
                                                    <div class="language-options">
                                                        <div v-for="lang in filteredLanguages" :key="lang.value"
                                                            class="language-option"
                                                            :class="{ selected: doctor.languagesSpoken.includes(lang.value) }"
                                                            @click="toggleLanguage(lang.value)">
                                                            {{ lang.label }}
                                                            <i v-if="doctor.languagesSpoken.includes(lang.value)"
                                                                class="fa-solid fa-check check-icon"></i>
                                                        </div>
                                                    </div>
                                                    <div class="language-dropdown-footer">
                                                        <button type="button" class="btn-close-dropdown"
                                                            @click="closeLanguageDropdown">
                                                            Done
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="validationErrors.languagesSpoken" class="validation-error">
                                                {{ validationErrors.languagesSpoken }}
                                            </div>
                                        </div>

                                        <!-- Q4: Bio -->
                                        <div class="col-md-6">
                                            <div class="form-field-wrapper">
                                                <label class="form-label-theme">Experience (Years) <span
                                                        class="required">*</span></label>
                                                <input v-model="doctor.experienceYears"
                                                    @blur="validateField('experienceYears')" type="number"
                                                    class="form-control-theme"
                                                    :class="{ 'is-invalid': validationErrors.experienceYears }" min="0"
                                                    max="50" />
                                                <div v-if="validationErrors.experienceYears" class="validation-error">
                                                    {{ validationErrors.experienceYears }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="form-field-wrapper">
                                                <label class="form-label-theme">Consultation Fee <span
                                                        class="required">*</span></label>
                                                <div class="input-theme-wrapper">
                                                    <input v-model="doctor.consultationFee"
                                                        @blur="validateField('consultationFee')" type="number"
                                                        class="form-control-theme"
                                                        :class="{ 'is-invalid': validationErrors.consultationFee }"
                                                        min="0" step="0.01" />
                                                    <div class="icon-indicator"><i class="fa-solid fa-money-bill"></i>
                                                    </div>
                                                </div>
                                                <div v-if="validationErrors.consultationFee" class="validation-error">
                                                    {{ validationErrors.consultationFee }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-12">
                                            <label class="form-label-theme">About Me <span
                                                    class="text-muted">(Optional)</span></label>
                                            <textarea v-model="doctor.aboutMe" @blur="validateField('aboutMe')"
                                                class="form-control-theme"
                                                :class="{ 'is-invalid': validationErrors.aboutMe }" rows="3"
                                                placeholder="Short professional bio..."></textarea>
                                        </div>

                                        <!-- Q5: Schedule Basics - Now with same height -->
                                        <!-- <div class="col-md-6">
                                            <div class="schedule-section">
                                                <label class="form-label-theme">General Sitting Time <span
                                                        class="required">*</span></label>
                                                <div class="time-range-wrapper">
                                                    <div class="time-range-inputs">
                                                        <div class="time-input-group">
                                                            <label>From</label>
                                                            <input type="time" v-model="sittingTimeStart"
                                                                @change="validateTimeRange"
                                                                class="form-control-theme time-input"
                                                                :class="{ 'is-invalid': validationErrors.sittingTime }" />
                                                        </div>
                                                        <div class="time-separator">to</div>
                                                        <div class="time-input-group">
                                                            <label>To</label>
                                                            <input type="time" v-model="sittingTimeEnd"
                                                                @change="validateTimeRange"
                                                                class="form-control-theme time-input"
                                                                :class="{ 'is-invalid': validationErrors.sittingTime }" />
                                                        </div>
                                                    </div>
                                                    <div class="time-display" v-if="sittingTimeStart && sittingTimeEnd">
                                                        <i class="fa-solid fa-clock me-2"></i>
                                                        {{ formatTime(sittingTimeStart) }} - {{
                                                            formatTime(sittingTimeEnd) }}
                                                    </div>
                                                    <div class="time-displaceholder" v-else>
                                                        <i class="fa-solid fa-clock me-2"></i>
                                                        Select time range
                                                    </div>
                                                    <div v-if="validationErrors.sittingTime"
                                                        class="validation-error time-validation-error">
                                                        {{ validationErrors.sittingTime }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-md-6">
                                            <div class="schedule-section">
                                                <label class="form-label-theme">General Working Days <span
                                                        class="required">*</span></label>
                                                <div class="days-selector">
                                                    <div class="days-grid">
                                                        <div v-for="day in weekDays" :key="day.value" class="day-option"
                                                            :class="{ selected: doctor.workingDays.includes(day.value) }"
                                                            @click="toggleWorkingDay(day.value)">
                                                            {{ day.label }}
                                                        </div>
                                                    </div>
                                                    <div class="selected-days-display"
                                                        v-if="doctor.workingDays.length > 0">
                                                        <small class="text-muted">Selected: {{ formatWorkingDays()
                                                        }}</small>
                                                    </div>
                                                    <div class="selected-days-display" v-else>
                                                        <small class="text-muted">No days selected</small>
                                                    </div>
                                                    <div v-if="validationErrors.workingDays"
                                                        class="validation-error days-validation-error">
                                                        {{ validationErrors.workingDays }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div> -->
                                    </div>
                                </div>

                                <!-- Navigation -->
                                <div class="step-actions schedule-btn mt-4">
                                    <!-- <button @click="validateAllAndNext" class="btn-theme-primary">
                                        Continue to Schedule <i class="fa-solid fa-arrow-right"></i>
                                    </button> -->
                                    <button class="btn-theme-nav finish" @click="validateAllAndNext">
                                        Continue to Schedule <i class="fa-solid fa-arrow-right"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Step 2: Schedule (AddDoctorTimeslots) -->
                            <div v-else-if="currentStep === 1" class="step-content">
                                <!-- We pass the doctor object. The component emits updates. -->
                                <!-- In the parent template -->
                                <AddDoctorTimeslots ref="timeSlotsForm" :doctor="doctor" :selected-days="selectedDays"
                                    :active-day="activeDay" :sitting-time="doctor.sittingTime"
                                    @update:selected-days="selectedDays = $event"
                                    @update:active-day="activeDay = $event" @update:doctor="handleDoctorUpdate"
                                    @show-toast="handleToast" @saveDoctor="saveDoctor"
                                    @profile-back="currentStep = 0" />

                                <!-- Final Save Action (Outside standard stepper of timeslots form to control overall save) -->
                                <div class="step-actions mt-5 border-top pt-4">
                                    <!-- <button @click="currentStep = 0" class="btn-theme-secondary me-2">Back</button> -->
                                    <!-- <button @click="saveDoctor" class="btn-theme-success" :disabled="isLoading">
                                        <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                                        {{ isLoading ? 'Saving...' : 'Complete & Save Doctor' }}
                                    </button> -->
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <FooterView />
        </div>
    </div>
</template>

<script>
import Header from "@/layout/Header.vue";
import FooterView from "@/layout/Footer.vue";
import AddDoctorTimeslots from "@/components/doctorprofile/AddDoctorTimeslots.vue";
import { DoctorsService } from "@/services/doctors.service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getCoreDigits, lowercaseString } from "@/utils/helpers";

dayjs.extend(utc);
dayjs.extend(timezone);

export default {
    name: "AddDoctorConversational",
    components: {
        Header,
        FooterView,
        AddDoctorTimeslots
    },
    data() {
        return {
            currentStep: 0, // 0: Profile, 1: Schedule
            error: "",
            isLoading: false,
            selectedDays: [],
            activeDay: "",
            showLanguageDropdown: false,
            languageSearch: "",
            sittingTimeStart: "",
            sittingTimeEnd: "",

            // Validation errors object
            validationErrors: {
                firstname: "",
                lastname: "",
                speciality: "",
                departmentCode: "",
                medicalDegree: "",
                gender: "",
                phone: "",
                email: "",
                languagesSpoken: "",
                experienceYears: "",
                consultationFee: "",
                aboutMe: "",
                sittingTime: "",
                workingDays: "",
                reference: "",
            },

            // Language options
            languageOptions: [
                { value: "English", label: "English" },
                { value: "Urdu", label: "Urdu" },
                { value: "Arabic", label: "Arabic" },
                { value: "German", label: "German" },
                { value: "French", label: "French" },
                { value: "Spanish", label: "Spanish" },
                { value: "Hindi", label: "Hindi" },
                { value: "Chinese", label: "Chinese" },
                { value: "Japanese", label: "Japanese" },
                { value: "Russian", label: "Russian" },
                { value: "Portuguese", label: "Portuguese" },
                { value: "Italian", label: "Italian" }
            ],

            // Week days
            weekDays: [
                { value: "Monday", label: "Mon" },
                { value: "Tuesday", label: "Tue" },
                { value: "Wednesday", label: "Wed" },
                { value: "Thursday", label: "Thu" },
                { value: "Friday", label: "Fri" },
                { value: "Saturday", label: "Sat" },
                { value: "Sunday", label: "Sun" }
            ],

            // Main Doctor Object
            doctor: {
                firstname: "",
                lastname: "",
                speciality: "",
                departmentCode: "",
                aboutMe: "",
                reference: "",
                experienceYears: null,
                gender: "",
                medicalDegree: "",
                languagesSpoken: ["English"], // Default English language
                phone: "",
                hospitalId: this.$store.state.user.currentUser.objectId,
                email: "",
                photo: "",
                consultationFee: null,
                workingDays: [], // Initialize as empty array
                sittingTime: "",
                startDate: "",
                endDate: "",
                days: [], // Will be populated by timeslots form
            },
        };
    },
    computed: {
        conversationMessage() {
            if (this.currentStep === 0) {
                return "Hello! 👋 I'm here to help you add a new doctor. Let's start with their basic information.";
            }
            if (this.currentStep === 1) {
                return "Great! Now let's set up the detailed weekly schedule using the tool below. You can auto-generate slots or add them manually.";
            }
            return "";
        },
        filteredLanguages() {
            if (!this.languageSearch) {
                return this.languageOptions;
            }
            const search = this.languageSearch.toLowerCase();
            return this.languageOptions.filter(lang =>
                lang.label.toLowerCase().includes(search) ||
                lang.value.toLowerCase().includes(search)
            );
        },
        // Computed property for working days display
        formattedWorkingDays() {
            return this.formatWorkingDays();
        },
        // Detect Canada/USA hospital by hospitalId
        isCanadaHospital() {
            // return isCanadaHospital();
            return true;
        },
        phonePlaceholder() {
            return 'Enter phone number';
        },
    },
    watch: {
        // sittingTimeStart(newVal) {
        //     if (newVal && this.sittingTimeEnd) {
        //         this.doctor.sittingTime = `${this.formatTime(newVal)} - ${this.formatTime(this.sittingTimeEnd)}`;
        //         this.validateTimeRange();
        //     }
        // },
        // sittingTimeEnd(newVal) {
        //     if (this.sittingTimeStart && newVal) {
        //         this.doctor.sittingTime = `${this.formatTime(this.sittingTimeStart)} - ${this.formatTime(newVal)}`;
        //         this.validateTimeRange();
        //     }
        // },
        // 'doctor.workingDays'() {
        //     this.validateField('workingDays');
        // }
    },
    methods: {
        handleToast({ type, message }) {
            this.$store.commit("toast/NEW", { type, message });
        },
        handleDoctorUpdate(updatedDoctor) {
            this.doctor = { ...this.doctor, ...updatedDoctor };
        },

        // Formatting methods
        formatDepartmentCode() {
            this.doctor.departmentCode = this.doctor.departmentCode.toUpperCase();
            this.validateField('departmentCode');
        },

        formatPhone() {
            // Keep only digits and leading +
            let val = this.doctor.phone.toString();
            // preserve leading + if user typed it
            const hasPlus = val.startsWith('+');
            val = val.replace(/\D/g, '');
            this.doctor.phone = hasPlus ? '+' + val : val;
            this.validateField('phone');
        },

        // Normalise a phone string to its core local digits (strips country prefix)
        getCoreDigits(phone, isCanada) {
            return getCoreDigits(phone, isCanada);
        },

        // Language methods
        toggleLanguageDropdown() {
            this.showLanguageDropdown = !this.showLanguageDropdown;
            this.languageSearch = "";
        },

        closeLanguageDropdown() {
            this.showLanguageDropdown = false;
            this.validateField('languagesSpoken');
        },

        toggleLanguage(language) {
            const index = this.doctor.languagesSpoken.indexOf(language);
            if (index === -1) {
                this.doctor.languagesSpoken.push(language);
            } else {
                this.doctor.languagesSpoken.splice(index, 1);
            }
        },

        removeLanguage(language) {
            const index = this.doctor.languagesSpoken.indexOf(language);
            if (index !== -1) {
                this.doctor.languagesSpoken.splice(index, 1);
            }
            this.validateField('languagesSpoken');
        },

        // Working days methods
        toggleWorkingDay(day) {
            const index = this.doctor.workingDays.indexOf(day);
            if (index === -1) {
                this.doctor.workingDays.push(day);
            } else {
                this.doctor.workingDays.splice(index, 1);
            }
            this.validateField('workingDays');
        },

        getDayLabel(dayValue) {
            const day = this.weekDays.find(d => d.value === dayValue);
            return day ? day.label : dayValue;
        },

        isConsecutiveDays(days) {
            if (days.length < 2) return false;

            const dayOrder = this.weekDays.map(d => d.value);
            const sortedDays = [...days].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

            for (let i = 1; i < sortedDays.length; i++) {
                const currentIndex = dayOrder.indexOf(sortedDays[i]);
                const prevIndex = dayOrder.indexOf(sortedDays[i - 1]);
                if (currentIndex !== prevIndex + 1) {
                    return false;
                }
            }
            return true;
        },

        formatWorkingDays() {
            if (!this.doctor.workingDays || this.doctor.workingDays.length === 0) {
                return "No days selected";
            }

            // Sort days in week order
            const dayOrder = this.weekDays.map(d => d.value);
            const sortedDays = [...this.doctor.workingDays].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

            // Format as readable string
            if (sortedDays.length === 7) {
                return "Every day";
            } else if (this.isConsecutiveDays(sortedDays)) {
                return `${this.getDayLabel(sortedDays[0])} - ${this.getDayLabel(sortedDays[sortedDays.length - 1])}`;
            } else {
                return sortedDays.map(day => this.getDayLabel(day)).join(", ");
            }
        },

        // Time methods
        formatTime(timeString) {
            if (!timeString) return "";
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;
            return `${formattedHour}:${minutes} ${ampm}`;
        },

        // Validation methods
        validateField(field) {
            // Clear previous error
            this.validationErrors[field] = "";

            switch (field) {
                case 'firstname':
                    if (!this.doctor.firstname.trim()) {
                        this.validationErrors.firstname = "First name is required";
                    } else if (this.doctor.firstname.trim().length < 2) {
                        this.validationErrors.firstname = "First name must be at least 2 characters";
                    }
                    break;

                case 'lastname':
                    if (!this.doctor.lastname.trim()) {
                        this.validationErrors.lastname = "Last name is required";
                    } else if (this.doctor.lastname.trim().length < 2) {
                        this.validationErrors.lastname = "Last name must be at least 2 characters";
                    }
                    break;

                case 'speciality':
                    if (!this.doctor.speciality.trim()) {
                        this.validationErrors.speciality = "Speciality is required";
                    }
                    break;

                case 'departmentCode':
                    if (!this.doctor.departmentCode.trim()) {
                        this.validationErrors.departmentCode = "Department code is required";
                    } else if (this.doctor.departmentCode.trim().length !== 2) {
                        this.validationErrors.departmentCode = "Department code must be exactly 2 letters";
                    } else if (!/^[A-Z]{2}$/.test(this.doctor.departmentCode)) {
                        this.validationErrors.departmentCode = "Department code must contain only letters";
                    }
                    break;

                case 'medicalDegree':
                    if (!this.doctor.medicalDegree.trim()) {
                        this.validationErrors.medicalDegree = "Medical degree is required";
                    }
                    break;

                case 'gender':
                    if (!this.doctor.gender) {
                        this.validationErrors.gender = "Please select a gender";
                    }
                    break;

                case 'phone': {
                    // const result = validatePhone(this.doctor.phone);
                    // if (!result.valid) this.validationErrors.phone = result.message;

                    if (!this.doctor.phone) {
                        this.validationErrors.phone = "Phone number is required";
                    }
                    break;
                }

                case 'email':
                    if (!this.doctor.email.trim()) {
                        this.validationErrors.email = "Email is required";
                    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.doctor.email)) {
                        this.validationErrors.email = "Please enter a valid email address";
                    }
                    break;

                case 'languagesSpoken':
                    if (this.doctor.languagesSpoken.length === 0) {
                        this.validationErrors.languagesSpoken = "At least one language is required";
                    }
                    break;

                case 'experienceYears':
                    if (!this.doctor.experienceYears && this.doctor.experienceYears !== 0) {
                        this.validationErrors.experienceYears = "Experience is required";
                    } else {
                        const years = parseInt(this.doctor.experienceYears);
                        if (isNaN(years) || years < 0 || years > 50) {
                            this.validationErrors.experienceYears = "Experience must be between 0-50 years";
                        }
                    }
                    break;

                case 'consultationFee':
                    if (!this.doctor.consultationFee && this.doctor.consultationFee !== 0) {
                        this.validationErrors.consultationFee = "Consultation fee is required";
                    } else {
                        const fee = parseFloat(this.doctor.consultationFee);
                        if (isNaN(fee) || fee < 0) {
                            this.validationErrors.consultationFee = "Consultation fee must be a positive number";
                        }
                    }
                    break;

                case 'aboutMe':
                    // Optional field, no validation required
                    break;

                // case 'sittingTime':
                //     this.validateTimeRange();
                //     break;

                // case 'workingDays':
                //     if (this.doctor.workingDays.length === 0) {
                //         this.validationErrors.workingDays = "Please select at least one working day";
                //     }
                //     break;
            }
        },

        validateTimeRange() {
            this.validationErrors.sittingTime = "";

            if (!this.sittingTimeStart || !this.sittingTimeEnd) {
                this.validationErrors.sittingTime = "Both start and end times are required";
            } else if (this.sittingTimeStart && this.sittingTimeEnd) {
                const startTime = new Date(`2000-01-01T${this.sittingTimeStart}`);
                const endTime = new Date(`2000-01-01T${this.sittingTimeEnd}`);

                if (startTime >= endTime) {
                    this.validationErrors.sittingTime = "End time must be after start time";
                }
            }
        },

        async validateAllAndNext() {
            // Clear all validation errors first
            Object.keys(this.validationErrors).forEach(key => {
                this.validationErrors[key] = "";
            });

            // Validate all required fields
            const fieldsToValidate = [
                'firstname', 'lastname', 'speciality', 'departmentCode', 'medicalDegree',
                'gender', 'phone', 'email', 'languagesSpoken', 'experienceYears',
                'consultationFee', 'sittingTime', 'workingDays'
            ];

            fieldsToValidate.forEach(field => this.validateField(field));

            // Check if there are any validation errors
            const hasErrors = Object.values(this.validationErrors).some(error => error !== "");

            if (hasErrors) {
                // Scroll to first error
                this.$nextTick(() => {
                    const errorElements = document.querySelectorAll('.validation-error');
                    const firstErrorElement = Array.from(errorElements).find(el => el.textContent.trim() !== '');
                    if (firstErrorElement) {
                        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
                return;
            }

            // Save doctor details before proceeding to schedule for ID generation
            const isSaved = await this.saveDoctor(true);

            if (isSaved) {
                this.currentStep = 1; // Go to Schedule step
            }
        },

        async saveDoctor(isIntermediate = false) {
            this.isLoading = true;

            // Basic final validation - Skip check if intermediate, as we are about to add slots
            if (!isIntermediate && (!this.doctor.days || this.doctor.days.length === 0)) {
                if (!confirm("No detailed schedule slots configured. Are you sure you want to save without slots?")) {
                    this.isLoading = false;
                    return false;
                }
            }

            try {
                // --- Normalize and sanitize data (Copied & Adapted from DoctorProfile.vue) ---
                const userOffset = dayjs().utcOffset();

                const formattedDoctor = {
                    ...this.doctor,
                    // If we have an ID, ensure it's included for updates
                    id: this.doctor.id || this.doctor._id || undefined,
                    phone: String(this.doctor.phone).replace(/\D/g, ""),
                    experienceYears: Number(this.doctor.experienceYears) || 0,
                    consultationFee: Number(this.doctor.consultationFee) || 0,
                    email: lowercaseString(this.doctor.email),
                    reference: this.doctor.reference,

                    // Convert dates to ISO UTC format (if set in timeslots form)
                    startDate: this.doctor.startDate
                        ? dayjs(this.doctor.startDate).startOf("day").utc(true).toISOString()
                        : null,
                    endDate: this.doctor.endDate
                        ? dayjs(this.doctor.endDate).startOf("day").utc(true).toISOString()
                        : null,

                    days: this.doctor.days.map((day) => {
                        const baseDate = dayjs(this.doctor.startDate || new Date());
                        // Convert entered local times using fixed offset
                        const startUtc = day.startTime
                            ? dayjs(`${baseDate.format("YYYY-MM-DD")}T${day.startTime}`)
                                .utcOffset(userOffset, true)
                                .utc()
                                .format("HH:mm")
                            : null;

                        const endUtc = day.endTime
                            ? dayjs(`${baseDate.format("YYYY-MM-DD")}T${day.endTime}`)
                                .utcOffset(userOffset, true)
                                .utc()
                                .format("HH:mm")
                            : null;

                        const slots = Array.isArray(day.slots)
                            ? day.slots.map((slot) => ({
                                startTime: dayjs(`${baseDate.format("YYYY-MM-DD")}T${slot.startTime}`)
                                    .utcOffset(userOffset, true)
                                    .utc()
                                    .format("HH:mm"),
                                endTime: dayjs(`${baseDate.format("YYYY-MM-DD")}T${slot.endTime}`)
                                    .utcOffset(userOffset, true)
                                    .utc()
                                    .format("HH:mm"),
                            }))
                            : [];

                        return {
                            ...day,
                            numberOfPatients: Number(day.numberOfPatients) || 0,
                            break: Number(day.break) || 0,
                            appointmentType: day.appointmentType || null,
                            startTime: startUtc,
                            endTime: endUtc,
                            slots,
                        };
                    }),
                };

                // 🧹 Clean up empty or invalid day entries
                formattedDoctor.days = formattedDoctor.days.filter(
                    (d) =>
                        d.day &&
                        d.day.trim() !== "" &&
                        d.appointmentType &&
                        d.appointmentType.trim() !== "" &&
                        d.startTime &&
                        d.endTime
                );

                // --- API call ---
                // If we have an ID, we update, otherwise create
                // But for intermediate save of NEW doctor, we use create. 
                // Afterwards, we might need update?
                // Assuming create returns the full object with ID
                let response;
                if (this.doctor.id) {
                    response = await DoctorsService.update(this.doctor.id, formattedDoctor);
                } else {
                    response = await DoctorsService.create(formattedDoctor);
                }

                if (response.success || response.data?.success) { // Handle potential different response structures
                    const savedData = response.data?.data || response.data || {};
                    // Update local doctor object with ID from server
                    if (savedData.id || savedData._id) {
                        this.doctor = { ...this.doctor, ...savedData, id: savedData.id || savedData._id };
                    }

                    if (!isIntermediate) {
                        this.handleToast({ type: "success", message: "Doctor saved successfully!" });
                        this.$router.push("/hospital-settings");
                    } else {
                        this.handleToast({ type: "success", message: "Doctor profile saved. Continuing to schedule..." });
                    }
                    return true;
                } else {
                    this.handleToast({ type: "error", message: response.message || "Failed to save doctor." });
                    return false;
                }

            } catch (e) {
                console.error(e);
                this.handleToast({ type: "error", message: "An error occurred while saving." });
                return false;
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #0144A6;
}

.wrapper,
.main,
.profile-doctor {
    height: 100vh;
}

.doctor-container {
    height: 50vh;
}

.page-content {
    width: 100%;
    margin: 150px auto;
    padding-bottom: 50px;
}

.page-header {
    width: 30%;
}

.wizard-card {
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.367);
    padding: 30px;

    height: calc(100vh - 160px);
    /* Header + footer space */
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    /* Prevent outer scroll */
}


/* Wizard Progress */
.wizard-progress {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;
    opacity: 0.5;
    transition: all 0.3s;
    min-width: 100px;
}

.step-item.active {
    opacity: 1;
}

.step-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #eee;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-bottom: 5px;
    transition: all 0.3s;
    font-size: small;
}

.step-item.active .step-circle {
    background: #3c78f9;
    color: white;
    box-shadow: 0 0 0 4px rgba(60, 120, 249, 0.2);
}

.step-item.completed .step-circle {
    background: #0144A6;
    color: white;
}

.step-line {
    flex: 1;
    height: 2px;
    background: #dcdbdb;
    margin: 0 20px;
    position: relative;
    top: -14px;
    z-index: 1;
}

.step-line.filled {
    background: #0144A6;
}

/* Conversation Header */
.conversation-header {
    display: flex;
    gap: 15px;
    align-items: flex-start;
    background: #f8faff;
    padding: 15px;
    border-radius: 10px;
    border-left: 4px solid #3c78f9;
}

.avatar-assistant {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #3c78f9;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.assistant-bubble {
    font-size: 16px;
    color: #333;
    font-weight: 500;
    line-height: 1.4;
}

/* Form Styles */
.form-label-theme {
    font-weight: 600;
    margin-bottom: 6px;
    font-size: 14px;
    color: #444;
}

.form-control-theme,
.form-select-theme {
    width: 100%;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.2s;
}

.form-control-theme:focus,
.form-select-theme:focus {
    border-color: #3c78f9;
    outline: none;
    box-shadow: 0 0 0 3px rgba(60, 120, 249, 0.1);
}

.form-control-theme.is-invalid,
.form-select-theme.is-invalid {
    border-color: #dc3545;
}

.form-control-theme.is-invalid:focus,
.form-select-theme.is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.required {
    color: #dc3545;
    font-weight: bold;
}

/* Form Field Wrapper */
.form-field-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.form-field-wrapper .form-control-theme,
.form-field-wrapper .input-theme-wrapper {
    margin-bottom: 0;
}

/* Validation Error Styles */
.validation-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
    font-weight: 500;
    min-height: 18px;
}

.btn-theme-primary {
    background: #3c78f9;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-primary:hover {
    background: #2a65e0;
}

.btn-theme-secondary {
    background: #f1f3f5;
    color: #333;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-secondary:hover {
    background: #e9ecef;
}

.btn-theme-success {
    background: #0144A6;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-success:hover {
    background: #0144A6;
}

.input-theme-wrapper {
    position: relative;
}

.input-theme-wrapper .form-control-theme {
    padding-right: 40px;
}

.input-theme-wrapper .icon-indicator {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
}

/* Schedule Sections - Same Height */
.schedule-section {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.schedule-section .form-label-theme {
    margin-bottom: 10px;
}

/* Enhanced Multi-select for Languages */
.multiselect-wrapper {
    position: relative;
}

.selected-languages {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 10px 15px;
    min-height: 50px;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s;
}

.selected-languages:hover {
    border-color: #3c78f9;
}

.selected-languages.is-invalid {
    border-color: #dc3545;
}

.selected-languages .placeholder {
    color: #999;
}

.selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.language-tag {
    background: #e9f2ff;
    color: #3c78f9;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.remove-tag {
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    color: #999;
}

.remove-tag:hover {
    color: #dc3545;
}

.dropdown-icon {
    color: #666;
    transition: transform 0.2s;
}

.language-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    margin-top: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
}

.language-search {
    padding: 10px;
    border-bottom: 1px solid #eee;
}

.search-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.language-options {
    max-height: 200px;
    overflow-y: auto;
}

.language-option {
    padding: 10px 15px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.language-option:hover {
    background: #f8faff;
}

.language-option.selected {
    background: #e9f2ff;
    color: #3c78f9;
}

.check-icon {
    color: #3c78f9;
}

.language-dropdown-footer {
    padding: 10px;
    border-top: 1px solid #eee;
    text-align: right;
}

.btn-close-dropdown {
    background: #3c78f9;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
}

/* Time Range Picker */
.time-range-wrapper {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    background: #fafafa;
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
}

.time-range-inputs {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 10px;
    flex: 1;
}

.time-input-group {
    flex: 1;
}

.time-input-group label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.time-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.time-input.is-invalid {
    border-color: #dc3545;
}

.time-separator {
    color: #666;
    font-weight: 600;
    margin-top: 20px;
}

.time-display,
.time-displaceholder {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    color: #333;
    display: flex;
    align-items: center;
    margin-top: 10px;
    min-height: 40px;
}

.time-displaceholder {
    color: #999;
    font-style: italic;
}

.time-validation-error {
    margin-top: 8px;
    margin-bottom: 0;
}

/* Days Selector */
.days-selector {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 30px;
    background: #fafafa;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
    margin-bottom: 10px;
    flex: 1;
}

.day-option {
    padding: 8px;
    text-align: center;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 13px;
    background: white;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.day-option:hover {
    border-color: #3c78f9;
    background: #f8faff;
}

.day-option.selected {
    background: #3c78f9;
    color: white;
    border-color: #3c78f9;
}

.selected-days-display {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    color: #666;
    min-height: 40px;
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.days-validation-error {
    margin-top: 0;
    margin-bottom: 0;
}

/* Transition Utils */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.fade-in {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.btn-theme-nav {
    border: none;
    font-family: var(--font-family);
    font-weight: 600;
    padding: 14px 35px;
    border-radius: var(--border-radius-btn);
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 12px;
    background: #0144A6;
    color: white;
    border-radius: 24px;
}

.btn-theme-nav:hover {
    background: #013e98;
    transform: translateY(-2px);
    box-shadow: 0 15px 25px rgba(0, 123, 255, 0.35);
}

.btn-theme-nav:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.schedule-btn {
    display: flex;
    justify-content: end;
}
</style>